/**
 * Theme Provider Context
 * Provides theme configuration and utilities throughout the application
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ThemeConfig, ThemeDefinition, ThemeContextValue } from '../types/theme';
import { TraceKitConfig } from '../types/config';
import { useThemeStore } from '../stores/theme';
import { getDefaultTheme, mergeThemeWithDefaults, applyThemeVariables } from '../utils/theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
  children: ReactNode;
  config: TraceKitConfig;
}

/**
 * Theme Provider Component
 * Manages theme state and provides theme values to all child components
 */
export function ThemeProvider({ children, config }: ThemeProviderProps) {
  const { theme: themeMode, setTheme, getEffectiveTheme } = useThemeStore();
  const themeConfig = config.theme || { defaultMode: 'dark', allowToggle: true };

  // When the app forces light mode (allowToggle: false + defaultMode: light),
  // always use 'light' regardless of what the store says. This prevents the Zustand
  // persist rehydration from temporarily reinstating a stale 'system'/'dark' theme.
  const forcedMode = themeConfig.allowToggle === false && themeConfig.defaultMode === 'light'
    ? 'light' as const
    : null;

  // Get effective theme mode (light or dark)
  const storeMode = getEffectiveTheme();
  const effectiveMode = forcedMode ?? storeMode;
  
  // Get the active theme definition based on mode
  const [currentTheme, setCurrentTheme] = useState<ThemeDefinition>(() => {
    return getDefaultTheme(effectiveMode, themeConfig, config);
  });
  
  // Update theme when mode changes
  useEffect(() => {
    const newTheme = getDefaultTheme(effectiveMode, themeConfig, config);
    setCurrentTheme(newTheme);
    
    // Apply CSS variables to DOM
    applyThemeVariables(newTheme);

    // Store the resolved ThemeDefinition so setTheme() can also apply CSS vars
    useThemeStore.getState().setThemeConfig(newTheme);
    
    // Update dark mode class on html element
    if (effectiveMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [effectiveMode, themeConfig, config]);
  
  // Toggle between light and dark modes
  const toggleTheme = () => {
    if (themeConfig.allowToggle) {
      setTheme(effectiveMode === 'dark' ? 'light' : 'dark');
    }
  };
  
  // Set specific theme mode ('auto' maps to 'system' for the store)
  const setMode = (mode: 'light' | 'dark' | 'auto') => {
    if (themeConfig.allowToggle) {
      setTheme(mode === 'auto' ? 'system' : mode);
    }
  };
  
  const contextValue: ThemeContextValue = {
    mode: effectiveMode,
    theme: currentTheme,
    colors: currentTheme.colors,
    setMode,
    toggleTheme,
    config: themeConfig,
  };
  
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 * @throws Error if used outside ThemeProvider
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
}

/**
 * Hook to get current theme colors (convenience hook)
 */
export function useThemeColors() {
  const { colors } = useTheme();
  return colors;
}

/**
 * Hook to get current theme mode
 */
export function useThemeMode() {
  const { mode, setMode, toggleTheme, config } = useTheme();
  return { mode, setMode, toggleTheme, allowToggle: config.allowToggle };
}

